import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap'
import InputMask from 'react-input-mask'

import cogoToast from 'cogo-toast'

import AdjustersService from '../../services/AdjustersService'
import CasesService from '../../services/CasesService'
import ProviderService from '../../services/ProviderService'
import { formatPhone } from '../../utils/PhoneUtils'
import FAIcon from '../FAIcon'

export const CONTACT_CATEGORIES = {
  CLIENT: {
    key: 'CLIENT',
    label: 'Client & Family',
    accentColor: '#17a2b8',
    badgeVariant: 'info',
    icon: 'user',
    sortOrder: 1,
  },
  CARRIER: {
    key: 'CARRIER',
    label: 'Claims & Carrier',
    accentColor: '#007bff',
    badgeVariant: 'primary',
    icon: 'shield-alt',
    sortOrder: 2,
  },
  MEDICAL: {
    key: 'MEDICAL',
    label: 'Medical Provider',
    accentColor: '#28a745',
    badgeVariant: 'success',
    icon: 'user-md',
    sortOrder: 3,
  },
  EMPLOYER: {
    key: 'EMPLOYER',
    label: 'Employer',
    accentColor: '#d39e00',
    badgeVariant: 'warning',
    icon: 'building',
    sortOrder: 4,
  },
  LEGAL: {
    key: 'LEGAL',
    label: 'Legal / Attorney',
    accentColor: '#495057',
    badgeVariant: 'dark',
    icon: 'balance-scale',
    sortOrder: 5,
  },
  ANCILLARY: {
    key: 'ANCILLARY',
    label: 'Ancillary / Vendor',
    accentColor: '#6f42c1',
    badgeVariant: 'secondary',
    icon: 'briefcase-medical',
    sortOrder: 6,
  },
}

const EXPANDED_STORAGE_KEY = 'gs_keyContactsSectionExpanded'

export function buildContactDedupKey(contact) {
  if (!contact) return ''
  if (contact.sourceKey) return contact.sourceKey
  const name = String(contact.name || '')
    .trim()
    .toLowerCase()
  const role = String(contact.role || '')
    .trim()
    .toLowerCase()
  const org = String(contact.organization || '')
    .trim()
    .toLowerCase()
  return `${name}|${role}|${org}`
}

function splitFullName(fullName = '') {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  }
}

function normalizeStr(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
}

export function extractAddress(obj) {
  const empty = { line1: '', city: '', state: '', zip: '' }
  if (!obj || typeof obj !== 'object') return empty

  // 1. Check Provider-style addresses array (PrimaryPractice -> Mailing -> first non-empty)
  if (Array.isArray(obj.addresses) && obj.addresses.length > 0) {
    const primary =
      obj.addresses.find(a => a && a.type === 'PrimaryPractice' && (a.line1 || a.city || a.state || a.zip)) ||
      obj.addresses.find(a => a && a.type === 'Mailing' && (a.line1 || a.city || a.state || a.zip)) ||
      obj.addresses.find(a => a && (a.line1 || a.city || a.state || a.zip))
    if (primary) {
      return {
        line1: String(primary.line1 || '').trim(),
        city: String(primary.city || '').trim(),
        state: String(primary.state || '').trim(),
        zip: String(primary.zip || '').trim(),
      }
    }
  }

  // 2. Check nested address object ({ line1, city, state, zip })
  const nested = obj.address || obj.contact?.address
  if (nested && typeof nested === 'object') {
    return {
      line1: String(nested.line1 || nested.addressLine1 || nested.street || '').trim(),
      city: String(nested.city || nested.addressCity || '').trim(),
      state: String(nested.state || nested.addressState || '').trim(),
      zip: String(nested.zip || nested.postalCode || nested.addressZip || '').trim(),
    }
  }

  // 3. Check flat address fields (e.g. Adjuster DTO)
  if (obj.addressLine1 || obj.addressCity || obj.addressState || obj.addressZip || obj.line1 || obj.city) {
    return {
      line1: String(obj.addressLine1 || obj.line1 || '').trim(),
      city: String(obj.addressCity || obj.city || '').trim(),
      state: String(obj.addressState || obj.state || '').trim(),
      zip: String(obj.addressZip || obj.zip || '').trim(),
    }
  }

  return empty
}

export function hasAddress(addr) {
  if (!addr || typeof addr !== 'object') return false
  return Boolean(
    String(addr.line1 || '').trim() ||
      String(addr.city || '').trim() ||
      String(addr.state || '').trim() ||
      String(addr.zip || '').trim(),
  )
}

export function formatAddress(addr) {
  if (!hasAddress(addr)) return ''
  const line1 = String(addr.line1 || '').trim()
  const city = String(addr.city || '').trim()
  const state = String(addr.state || '').trim()
  const zip = String(addr.zip || '').trim()
  const stateZip = [state, zip].filter(Boolean).join(' ')
  const locality = [city, stateZip].filter(Boolean).join(', ')
  return [line1, locality].filter(Boolean).join(', ')
}

function mergeAddress(primary, fallback) {
  const p = extractAddress({ address: primary })
  const f = extractAddress({ address: fallback })
  if (hasAddress(p)) {
    return {
      line1: p.line1 || f.line1 || '',
      city: p.city || f.city || '',
      state: p.state || f.state || '',
      zip: p.zip || f.zip || '',
    }
  }
  return f
}

function buildCleanAddress(addr) {
  return {
    line1: String(addr?.line1 || '').trim() || null,
    city: String(addr?.city || '').trim() || null,
    state: String(addr?.state || '').trim() || null,
    zip: String(addr?.zip || '').trim() || null,
  }
}

export function aggregateCaseContacts({ caseModel, linkedProviders = [], resolvedAdjuster = null }) {
  if (!caseModel) return { visibleCards: [], hiddenCards: [], visibleContacts: [], hiddenContacts: [] }

  const hiddenKeys = new Set(Array.isArray(caseModel.hiddenKeyContactKeys) ? caseModel.hiddenKeyContactKeys : [])
  const cardsMap = new Map()

  const addOrMergeCard = card => {
    if (!card || !card.sourceKey) return
    const normalizedCardAddress = extractAddress(card)
    if (!cardsMap.has(card.sourceKey)) {
      cardsMap.set(card.sourceKey, {
        ...card,
        address: normalizedCardAddress,
        subContacts: Array.isArray(card.subContacts) ? [...card.subContacts] : [],
      })
    } else {
      const existing = cardsMap.get(card.sourceKey)
      const mergedSubs = [...(existing.subContacts || [])]
      ;(card.subContacts || []).forEach(sc => {
        const dup = mergedSubs.some(
          m => normalizeStr(m.name) === normalizeStr(sc.name) && normalizeStr(m.role) === normalizeStr(sc.role),
        )
        if (!dup) mergedSubs.push(sc)
      })
      cardsMap.set(card.sourceKey, {
        ...existing,
        organization: existing.organization || card.organization,
        phone: existing.phone || card.phone,
        extension: existing.extension || card.extension,
        fax: existing.fax || card.fax,
        email: existing.email || card.email,
        directAddress: existing.directAddress || card.directAddress,
        address: mergeAddress(existing.address, normalizedCardAddress),
        notes: existing.notes || card.notes,
        subContacts: mergedSubs,
      })
    }
  }

  // 1. CLIENT / CLAIMANT CARD (Always present on a case)
  const client = caseModel.client || {}
  const clientName = [client.firstName, client.lastName].filter(Boolean).join(' ').trim() || 'Client / Claimant'
  const clientSubContacts = []
  if (client.alternateContact) {
    const alt = client.alternateContact
    const altName = [alt.firstName, alt.lastName].filter(Boolean).join(' ').trim()
    if (altName || alt.phone || alt.email) {
      clientSubContacts.push({
        id: 'client-alt-contact',
        name: altName || 'Alternate Contact',
        role: 'Alternate / Emergency Contact',
        phone: alt.phone || '',
        fax: alt.fax || '',
        email: alt.email || '',
        isBuiltInAlt: true,
      })
    }
  }
  const clientNotesParts = []
  if (client.firstLanguage && normalizeStr(client.firstLanguage) !== 'english') {
    clientNotesParts.push(`Language: ${client.firstLanguage}`)
  }
  if (client.occupation) {
    clientNotesParts.push(client.occupation)
  }
  addOrMergeCard({
    key: 'entity:client',
    sourceKey: 'entity:client',
    entityType: 'client',
    category: 'CLIENT',
    name: clientName,
    role: 'Claimant / Patient',
    organization: '',
    phone: client.phone || client.contact?.phone || '',
    extension: '',
    fax: client.fax || '',
    email: client.email || '',
    address: extractAddress(client),
    notes: clientNotesParts.join(' • '),
    isPrimary: true,
    isCustom: false,
    subContacts: clientSubContacts,
  })

  // 2. CLAIMS ADJUSTER / CARRIER CARD (Always present on a case)
  const referral = caseModel.referral || {}
  const adjObj =
    resolvedAdjuster || (referral.adjuster && typeof referral.adjuster === 'object' ? referral.adjuster : null)
  const adjName =
    adjObj?.name ||
    [adjObj?.firstName, adjObj?.lastName].filter(Boolean).join(' ').trim() ||
    referral.adjusterName ||
    (typeof referral.adjuster === 'string' && !/^[0-9a-f-]{36}$/i.test(referral.adjuster) ? referral.adjuster : '') ||
    'Claims Adjuster'
  const adjPhone = adjObj?.phone || referral.adjusterPhone || referral.phone || ''
  const adjEmail = adjObj?.email || referral.adjusterEmail || referral.email || ''
  const adjFax = adjObj?.fax || referral.adjusterFax || ''
  const adjOrg =
    adjObj?.insurerName ||
    adjObj?.company ||
    referral.insuranceCompany ||
    referral.insurerName ||
    referral.insuranceCarrier ||
    caseModel.insurer?.name ||
    ''
  addOrMergeCard({
    key: 'entity:adjuster',
    sourceKey: 'entity:adjuster',
    entityType: 'adjuster',
    adjusterId: adjObj?.id || referral.adjusterId || null,
    category: 'CARRIER',
    name: adjName,
    role: 'Claims Adjuster',
    organization: adjOrg,
    phone: adjPhone,
    extension: adjObj?.extension || referral.adjusterExtension || '',
    fax: adjFax,
    email: adjEmail,
    address: extractAddress(adjObj || referral),
    notes: referral.claimNumber ? `Claim #${referral.claimNumber}` : adjObj?.notes || '',
    isPrimary: true,
    isCustom: false,
    subContacts: [],
  })

  // 3. EMPLOYER CARD (Surfaced when employer name or contact exists)
  const emp = caseModel.employer || {}
  const empContact = emp.contact || {}
  const empContactName =
    empContact.name ||
    [empContact.firstName, empContact.lastName].filter(Boolean).join(' ').trim() ||
    emp.contactName ||
    ''
  const empPhone = empContact.phone || emp.phone || ''
  const empEmail = empContact.email || emp.email || ''
  const empFax = empContact.fax || emp.fax || ''
  const empAddress = extractAddress(emp)
  if (emp.name || empContactName || empPhone || empEmail || hasAddress(empAddress)) {
    addOrMergeCard({
      key: 'entity:employer',
      sourceKey: 'entity:employer',
      entityType: 'employer',
      category: 'EMPLOYER',
      name: empContactName || emp.name || 'Employer Contact',
      role: empContact.title || 'Employer Contact',
      organization: emp.name && empContactName ? emp.name : '',
      phone: empPhone,
      extension: empContact.extension || '',
      fax: empFax,
      email: empEmail,
      address: empAddress,
      notes: emp.occupation ? `Occupation: ${emp.occupation}` : '',
      isPrimary: false,
      isCustom: false,
      subContacts: [],
    })
  }

  // 4. LEGAL / ATTORNEYS (Plaintiff / Claimant Attorney & Defense Attorney)
  const plaintiff = caseModel.plaintiff || {}
  const pContact = plaintiff.contact || {}
  const pContactName = [pContact.firstName, pContact.lastName].filter(Boolean).join(' ').trim()
  const pAddress = extractAddress(plaintiff)
  if (plaintiff.name || pContactName || pContact.phone || pContact.email || hasAddress(pAddress)) {
    addOrMergeCard({
      key: 'entity:plaintiff-attorney',
      sourceKey: 'entity:plaintiff-attorney',
      entityType: 'plaintiff',
      category: 'LEGAL',
      name: pContactName || plaintiff.name || 'Claimant Attorney',
      role: pContact.title || 'Claimant / Plaintiff Attorney',
      organization: plaintiff.name && pContactName ? plaintiff.name : '',
      phone: pContact.phone || '',
      fax: pContact.fax || '',
      email: pContact.email || '',
      address: pAddress,
      notes: '',
      isPrimary: false,
      isCustom: false,
      subContacts: [],
    })
  }

  const defendant = caseModel.defendant || {}
  const dContact = defendant.contact || {}
  const dContactName = [dContact.firstName, dContact.lastName].filter(Boolean).join(' ').trim()
  const dAddress = extractAddress(defendant)
  if (defendant.name || dContactName || dContact.phone || dContact.email || hasAddress(dAddress)) {
    addOrMergeCard({
      key: 'entity:defense-attorney',
      sourceKey: 'entity:defense-attorney',
      entityType: 'defendant',
      category: 'LEGAL',
      name: dContactName || defendant.name || 'Defense Attorney',
      role: dContact.title || 'Defense Attorney',
      organization: defendant.name && dContactName ? defendant.name : '',
      phone: dContact.phone || '',
      fax: dContact.fax || '',
      email: dContact.email || '',
      address: dAddress,
      notes: '',
      isPrimary: false,
      isCustom: false,
      subContacts: [],
    })
  }

  // 5. LINKED LIBRARY PROVIDERS (Treatment, Pharmacy, Equipment/DME, IME — with Staff & Office Contacts nested inside!)
  const providerList = Array.isArray(linkedProviders) ? linkedProviders : []
  providerList.forEach(prov => {
    if (!prov) return
    const provId = prov.id || prov.providerId
    const provName =
      prov.name || [prov.firstName, prov.lastName].filter(Boolean).join(' ').trim() || prov.organizationName || ''
    if (!provName) return
    const orgName =
      prov.classification === 'Individual' ? prov.organizationName || '' : prov.organizationName || prov.name || ''

    const staffList = Array.isArray(prov.staffContacts) ? prov.staffContacts : []
    const nestedStaff = staffList
      .filter(sc => sc && (sc.name || sc.phone || sc.email || sc.directAddress))
      .map((sc, sIdx) => ({
        id: sc.id || `staff-${sIdx}`,
        name: sc.name || 'Office Staff',
        role: sc.role || 'Medical Assistant',
        phone: sc.phone || '',
        extension: sc.extension || '',
        email: sc.email || '',
        directAddress: sc.directAddress || '',
        notes: sc.notes || '',
        isPrimary: Boolean(sc.isPrimary),
        isProviderStaff: true,
        staffIndex: sIdx,
      }))

    const provTypeNorm = normalizeStr(prov.type)
    let provCategory = 'MEDICAL'
    let provIconOverride = null
    let provRole = prov.specialty || prov.type || 'Treating Provider'

    if (provTypeNorm === 'pharmacy') {
      provCategory = 'ANCILLARY'
      provIconOverride = 'pills'
      provRole = prov.specialty || 'Pharmacy'
    } else if (provTypeNorm === 'equipment' || provTypeNorm === 'dme') {
      provCategory = 'ANCILLARY'
      provIconOverride = 'wheelchair'
      provRole = prov.specialty || 'DME / Equipment'
    } else if (provTypeNorm === 'ime') {
      provCategory = 'ANCILLARY'
      provIconOverride = 'stethoscope'
      provRole = prov.specialty ? `IME (${prov.specialty})` : 'IME Provider'
    }

    const sourceKey = `provider:${provId || normalizeStr(provName)}`
    addOrMergeCard({
      key: sourceKey,
      sourceKey,
      entityType: 'provider',
      providerType: prov.type || 'Treatment',
      category: provCategory,
      iconOverride: provIconOverride,
      providerId: provId,
      name: provName,
      role: provRole,
      organization: orgName && orgName !== provName ? orgName : '',
      phone: prov.phone || '',
      fax: prov.fax || '',
      email: prov.email || '',
      address: extractAddress(prov),
      notes: prov.notes || '',
      isPrimary: false,
      isCustom: false,
      subContacts: nestedStaff,
    })
  })

  // Fallback to caseModel.treatmentProviders / caseModel.providers / caseModel.treatment.providers
  const caseTreatmentProviders = [
    ...(Array.isArray(caseModel.treatmentProviders) ? caseModel.treatmentProviders : []),
    ...(Array.isArray(caseModel.providers) ? caseModel.providers : []),
    ...(Array.isArray(caseModel.treatment?.providers) ? caseModel.treatment.providers : []),
  ]
  caseTreatmentProviders.forEach((tp, tpIdx) => {
    if (!tp) return
    const tpName = tp.name || [tp.firstName, tp.lastName].filter(Boolean).join(' ').trim() || tp.organizationName || ''
    if (!tpName) return
    const existingCard = Array.from(cardsMap.values()).find(
      c =>
        c.entityType === 'provider' &&
        ((tp.id && String(c.providerId) === String(tp.id)) || normalizeStr(c.name) === normalizeStr(tpName)),
    )
    if (existingCard) {
      existingCard.address = mergeAddress(existingCard.address, extractAddress(tp))
      existingCard.phone = existingCard.phone || tp.phone || ''
      existingCard.fax = existingCard.fax || tp.fax || ''
      existingCard.email = existingCard.email || tp.email || ''
    } else {
      const sourceKey = `case-provider:${normalizeStr(tp.id || tpName)}`
      const orgName = tp.organizationName || tp.companyName || tp.practiceName || ''
      addOrMergeCard({
        key: sourceKey,
        sourceKey,
        entityType: 'caseProvider',
        caseProviderIndex: tpIdx,
        category: 'MEDICAL',
        providerId: tp.id || null,
        name: tpName,
        role: tp.specialty || tp.speciality || tp.type || 'Treating Provider',
        organization: orgName && orgName !== tpName ? orgName : '',
        phone: tp.phone || '',
        fax: tp.fax || '',
        email: tp.email || '',
        address: extractAddress(tp),
        notes: tp.notes || '',
        isPrimary: Boolean(tp.isPrimaryCarePhysician),
        isCustom: false,
        subContacts: [],
      })
    }
  })

  // 6. ANCILLARY & SPECIALISTS (Physician Reviewer / IME, Pharmacies, DME Equipment)
  const reviewer = caseModel.physicianReviewer
  if (reviewer && (reviewer.name || reviewer.companyName || reviewer.contact?.phone || reviewer.contact?.email)) {
    addOrMergeCard({
      key: 'entity:physician-reviewer',
      sourceKey: 'entity:physician-reviewer',
      entityType: 'physicianReviewer',
      category: 'ANCILLARY',
      iconOverride: 'stethoscope',
      name: reviewer.name || reviewer.companyName || 'Physician Reviewer',
      role: reviewer.specialization ? `IME / Reviewer (${reviewer.specialization})` : 'IME / Physician Reviewer',
      organization: reviewer.companyName && reviewer.name ? reviewer.companyName : '',
      phone: reviewer.contact?.phone || '',
      fax: reviewer.contact?.fax || '',
      email: reviewer.contact?.email || '',
      address: extractAddress(reviewer),
      notes: '',
      isPrimary: false,
      isCustom: false,
      subContacts: [],
    })
  }

  ;(Array.isArray(caseModel.pharmacies) ? caseModel.pharmacies : []).forEach((pharm, idx) => {
    if (!pharm || (!pharm.name && !pharm.phone && !pharm.email && !hasAddress(extractAddress(pharm)))) return
    const pharmId = pharm.id || pharm.providerId
    const existingLinkedPharm = Array.from(cardsMap.values()).find(
      c =>
        c.entityType === 'provider' &&
        ((pharmId && String(c.providerId) === String(pharmId)) ||
          (pharm.name && normalizeStr(c.name) === normalizeStr(pharm.name))),
    )
    if (existingLinkedPharm) {
      existingLinkedPharm.category = 'ANCILLARY'
      existingLinkedPharm.iconOverride = existingLinkedPharm.iconOverride || 'pills'
      existingLinkedPharm.role =
        existingLinkedPharm.role === 'Treating Provider' ? 'Pharmacy' : existingLinkedPharm.role || 'Pharmacy'
      existingLinkedPharm.address = mergeAddress(existingLinkedPharm.address, extractAddress(pharm))
      existingLinkedPharm.phone = existingLinkedPharm.phone || pharm.phone || ''
      existingLinkedPharm.fax = existingLinkedPharm.fax || pharm.fax || ''
      existingLinkedPharm.email = existingLinkedPharm.email || pharm.email || ''
      existingLinkedPharm.pharmacyIndex = idx
      return
    }

    const sourceKey = `pharmacy:${normalizeStr(pharm.name || idx)}`
    addOrMergeCard({
      key: sourceKey,
      sourceKey,
      entityType: 'pharmacy',
      pharmacyIndex: idx,
      providerId: pharmId || null,
      category: 'ANCILLARY',
      iconOverride: 'pills',
      name: pharm.name || 'Pharmacy',
      role: 'Pharmacy',
      organization: '',
      phone: pharm.phone || '',
      fax: pharm.fax || '',
      email: pharm.email || '',
      address: extractAddress(pharm),
      notes: '',
      isPrimary: false,
      isCustom: false,
      subContacts: [],
    })
  })
  ;(Array.isArray(caseModel.equipmentProviders) ? caseModel.equipmentProviders : []).forEach((eq, idx) => {
    if (!eq || (!eq.name && !eq.phone && !eq.email && !hasAddress(extractAddress(eq)))) return
    const eqId = eq.id || eq.providerId
    const existingLinkedEq = Array.from(cardsMap.values()).find(
      c =>
        c.entityType === 'provider' &&
        ((eqId && String(c.providerId) === String(eqId)) ||
          (eq.name && normalizeStr(c.name) === normalizeStr(eq.name))),
    )
    if (existingLinkedEq) {
      existingLinkedEq.category = 'ANCILLARY'
      existingLinkedEq.iconOverride = existingLinkedEq.iconOverride || 'wheelchair'
      existingLinkedEq.role =
        existingLinkedEq.role === 'Treating Provider' ? 'DME / Equipment' : existingLinkedEq.role || 'DME / Equipment'
      existingLinkedEq.address = mergeAddress(existingLinkedEq.address, extractAddress(eq))
      existingLinkedEq.phone = existingLinkedEq.phone || eq.phone || ''
      existingLinkedEq.fax = existingLinkedEq.fax || eq.fax || ''
      existingLinkedEq.email = existingLinkedEq.email || eq.email || ''
      existingLinkedEq.equipmentIndex = idx
      return
    }

    const sourceKey = `equipment:${normalizeStr(eq.name || idx)}`
    addOrMergeCard({
      key: sourceKey,
      sourceKey,
      entityType: 'equipmentProvider',
      equipmentIndex: idx,
      providerId: eqId || null,
      category: 'ANCILLARY',
      iconOverride: 'wheelchair',
      name: eq.name || 'Equipment / DME Provider',
      role: 'DME / Equipment',
      organization: '',
      phone: eq.phone || '',
      fax: eq.fax || '',
      email: eq.email || '',
      address: extractAddress(eq),
      notes: '',
      isPrimary: false,
      isCustom: false,
      subContacts: [],
    })
  })

  // 7. CURATED CASE KEY CONTACTS (Group under matching entity card if affiliated, or create standalone card)
  if (Array.isArray(caseModel.keyContacts)) {
    caseModel.keyContacts.forEach((kc, idx) => {
      if (!kc || (!kc.name && !kc.phone && !kc.email && !hasAddress(extractAddress(kc)))) return
      const kcOrg = kc.organization || kc.organizationOrAffiliation || kc.organizationOrProviderName || ''
      const kcParentKey = kc.parentEntityKey || kc.sourceEntityId || ''

      // Check if this contact belongs to an existing card (by parentEntityKey, providerId, or matching name/org)
      let targetParentCard = null
      if (kcParentKey && cardsMap.has(kcParentKey)) {
        targetParentCard = cardsMap.get(kcParentKey)
      } else {
        const allCards = Array.from(cardsMap.values())
        targetParentCard = allCards.find(c => {
          if (kcParentKey && c.providerId && String(c.providerId) === String(kcParentKey)) return true
          if (
            kcOrg &&
            (normalizeStr(c.name) === normalizeStr(kcOrg) ||
              (c.organization && normalizeStr(c.organization) === normalizeStr(kcOrg)))
          ) {
            return true
          }
          return false
        })
      }

      if (targetParentCard) {
        // Avoid duplicating if already present in targetParentCard.subContacts or if it IS the primary contact
        const isSameAsParent =
          normalizeStr(targetParentCard.name) === normalizeStr(kc.name) &&
          normalizeStr(targetParentCard.role) === normalizeStr(kc.role)
        const alreadyInSubs = (targetParentCard.subContacts || []).some(
          sc => normalizeStr(sc.name) === normalizeStr(kc.name) && normalizeStr(sc.role) === normalizeStr(kc.role),
        )
        if (isSameAsParent) {
          targetParentCard.phone = targetParentCard.phone || kc.phone || ''
          targetParentCard.extension = targetParentCard.extension || kc.extension || ''
          targetParentCard.email = targetParentCard.email || kc.email || ''
          targetParentCard.directAddress = targetParentCard.directAddress || kc.directAddress || ''
          targetParentCard.address = mergeAddress(targetParentCard.address, extractAddress(kc))
          targetParentCard.notes = targetParentCard.notes || kc.notes || ''
        } else if (!alreadyInSubs) {
          targetParentCard.subContacts.push({
            id: kc.id || `kc-sub-${idx}`,
            name: kc.name || 'Contact',
            role: kc.role || 'Staff Contact',
            phone: kc.phone || '',
            extension: kc.extension || '',
            email: kc.email || '',
            directAddress: kc.directAddress || '',
            notes: kc.notes || '',
            isPrimary: Boolean(kc.isPrimary),
            isCustomCaseContact: true,
            customIndex: idx,
          })
        }
      } else {
        // Standalone custom contact card — infer category from role/contactType
        const roleLower = normalizeStr(kc.role || kc.contactType)
        let inferredCategory = 'ANCILLARY'
        if (roleLower.includes('adjuster') || roleLower.includes('carrier') || roleLower.includes('claim')) {
          inferredCategory = 'CARRIER'
        } else if (roleLower.includes('employer') || roleLower.includes('hr') || roleLower.includes('supervisor')) {
          inferredCategory = 'EMPLOYER'
        } else if (roleLower.includes('attorney') || roleLower.includes('legal') || roleLower.includes('paralegal')) {
          inferredCategory = 'LEGAL'
        } else if (
          roleLower.includes('medical') ||
          roleLower.includes('nurse') ||
          roleLower.includes('doctor') ||
          roleLower.includes('provider') ||
          roleLower.includes('coordinator') ||
          roleLower.includes('scheduler')
        ) {
          inferredCategory = 'MEDICAL'
        } else if (roleLower.includes('client') || roleLower.includes('family') || roleLower.includes('spouse')) {
          inferredCategory = 'CLIENT'
        }

        const sourceKey = kc.id ? `custom:${kc.id}` : `custom:${normalizeStr(kc.name || idx)}:${normalizeStr(kc.role)}`
        addOrMergeCard({
          key: sourceKey,
          sourceKey,
          id: kc.id || `kc-${idx}`,
          entityType: 'custom',
          category: kc.category || inferredCategory,
          name: kc.name || 'Contact',
          role: kc.role || 'Key Contact',
          organization: kcOrg,
          phone: kc.phone || '',
          extension: kc.extension || '',
          fax: kc.fax || '',
          email: kc.email || '',
          directAddress: kc.directAddress || '',
          address: extractAddress(kc),
          notes: kc.notes || '',
          isPrimary: Boolean(kc.isPrimary),
          isCustom: true,
          customIndex: idx,
          subContacts: Array.isArray(kc.subContacts) ? kc.subContacts : [],
        })
      }
    })
  }

  const allCards = Array.from(cardsMap.values()).sort((a, b) => {
    const orderA = CONTACT_CATEGORIES[a.category]?.sortOrder || 99
    const orderB = CONTACT_CATEGORIES[b.category]?.sortOrder || 99
    if (orderA !== orderB) return orderA - orderB
    return 0
  })

  const visibleCards = allCards.filter(c => !hiddenKeys.has(c.sourceKey))
  const hiddenCards = allCards.filter(c => hiddenKeys.has(c.sourceKey))

  return {
    visibleCards,
    hiddenCards,
    // Keep backwards-compatible aliases for existing tests/consumers
    visibleContacts: visibleCards,
    hiddenContacts: hiddenCards,
  }
}

const KeyContactsCard = ({ caseNumber, caseModel, setCaseModel, canEdit = true, onActivityLogged }) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_STORAGE_KEY)
      return saved === null ? true : saved === 'true'
    } catch {
      return true
    }
  })
  const [viewingCardKey, setViewingCardKey] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [linkedProviders, setLinkedProviders] = useState([])
  const [resolvedAdjuster, setResolvedAdjuster] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCard, setEditingCard] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [loggingCallKey, setLoggingCallKey] = useState(null)
  const nameInputRef = useRef(null)
  const editNameInputRef = useRef(null)

  const [newContact, setNewContact] = useState({
    name: '',
    role: 'Medical Assistant',
    category: 'MEDICAL',
    organization: '',
    parentCardKey: '',
    saveToProviderDirectory: true,
    phone: '',
    extension: '',
    fax: '',
    email: '',
    directAddress: '',
    address: { line1: '', city: '', state: '', zip: '' },
    notes: '',
    isPrimary: false,
  })

  const preserveScrollPosition = callback => {
    const savedScrollX = window.scrollX || window.pageXOffset || 0
    const savedScrollY = window.scrollY || window.pageYOffset || 0
    callback()
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        window.scrollTo(savedScrollX, savedScrollY)
      })
    }
  }

  const toggleSectionExpanded = e => {
    if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') {
      e.currentTarget.blur()
    }
    preserveScrollPosition(() => {
      setIsSectionExpanded(prev => {
        const next = !prev
        try {
          localStorage.setItem(EXPANDED_STORAGE_KEY, String(next))
        } catch {
          // ignore storage error
        }
        return next
      })
    })
  }

  const handleOpenDetailModal = (card, e) => {
    if (e) {
      e.stopPropagation()
    }
    if (!card) return
    setViewingCardKey(card.sourceKey)
  }

  const handleLogCallActivity = async (card, e) => {
    if (e) {
      e.stopPropagation()
    }
    if (!card || !caseNumber) return
    setLoggingCallKey(card.sourceKey)
    try {
      const today = new Date().toISOString().split('T')[0]
      const rolePart = card.role ? ` (${card.role})` : ''
      await CasesService.logBillableActivity(caseNumber, {
        type: 'T-Con',
        time: 0.2,
        date: today,
        description: `Telephonic communication / case coordination with ${card.name}${rolePart}.`,
      })
      cogoToast.success(`Logged 0.2h T-Con with ${card.name}`)
      if (onActivityLogged) {
        onActivityLogged()
      }
    } catch (_err) {
      cogoToast.error('Failed to log call activity')
    } finally {
      setLoggingCallKey(null)
    }
  }

  const handleCopyValue = (value, label, e) => {
    if (e) e.stopPropagation()
    if (!value) return
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(() => {
        cogoToast.success(`Copied ${label} to clipboard`)
      })
    }
  }

  const loadLinkedEntities = useCallback(async () => {
    if (!caseNumber) return
    try {
      const provs = await ProviderService.getProvidersForCase(caseNumber)
      if (Array.isArray(provs)) {
        setLinkedProviders(provs)
      }
    } catch {
      // Non-fatal fallback to caseModel.treatmentProviders
    }
  }, [caseNumber])

  useEffect(() => {
    loadLinkedEntities()
  }, [loadLinkedEntities])

  // Fetch Adjuster by referral.adjusterId (integer ID) or referral.adjuster (string ID)
  useEffect(() => {
    const adjId = caseModel?.referral?.adjusterId
    const adjRef = caseModel?.referral?.adjuster
    const targetId = adjId != null ? adjId : typeof adjRef === 'string' && adjRef.trim() ? adjRef.trim() : null
    if (targetId != null && targetId !== '') {
      AdjustersService.get(targetId)
        .then(adj => {
          if (adj) setResolvedAdjuster(adj)
        })
        .catch(() => {})
    } else {
      setResolvedAdjuster(null)
    }
  }, [caseModel?.referral?.adjusterId, caseModel?.referral?.adjuster])

  const { visibleCards, hiddenCards } = useMemo(
    () =>
      aggregateCaseContacts({
        caseModel,
        linkedProviders,
        resolvedAdjuster,
      }),
    [caseModel, linkedProviders, resolvedAdjuster],
  )

  const viewingCard = useMemo(
    () => (viewingCardKey ? visibleCards.find(c => c.sourceKey === viewingCardKey) || null : null),
    [visibleCards, viewingCardKey],
  )

  const filteredCards = useMemo(() => {
    if (categoryFilter === 'ALL') return visibleCards
    return visibleCards.filter(c => c.category === categoryFilter)
  }, [visibleCards, categoryFilter])

  const availableCategories = useMemo(() => {
    const counts = {}
    visibleCards.forEach(c => {
      counts[c.category] = (counts[c.category] || 0) + 1
    })
    return counts
  }, [visibleCards])

  const handleHideOrRemoveCard = async (card, e) => {
    if (e) e.stopPropagation()
    if (!caseModel || isSaving) return
    setIsSaving(true)
    try {
      const updatedCase = { ...caseModel }
      if (card.isCustom && Array.isArray(updatedCase.keyContacts)) {
        updatedCase.keyContacts = updatedCase.keyContacts.filter((_, idx) => idx !== card.customIndex)
      } else {
        const currentHidden = Array.isArray(updatedCase.hiddenKeyContactKeys)
          ? [...updatedCase.hiddenKeyContactKeys]
          : []
        if (!currentHidden.includes(card.sourceKey)) {
          currentHidden.push(card.sourceKey)
        }
        updatedCase.hiddenKeyContactKeys = currentHidden
      }
      await CasesService.update(updatedCase)
      if (setCaseModel) setCaseModel(updatedCase)
      if (viewingCardKey === card.sourceKey) setViewingCardKey(null)
      cogoToast.info(`Hidden "${card.name}" from this case's Key Contacts`)
    } catch (err) {
      cogoToast.error(err.message || 'Failed to update Key Contacts')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRestoreHiddenContacts = async e => {
    if (e) e.stopPropagation()
    if (!caseModel || isSaving) return
    setIsSaving(true)
    try {
      const updatedCase = {
        ...caseModel,
        hiddenKeyContactKeys: [],
      }
      await CasesService.update(updatedCase)
      if (setCaseModel) setCaseModel(updatedCase)
      cogoToast.success('Restored hidden case contacts')
    } catch (err) {
      cogoToast.error(err.message || 'Failed to restore hidden contacts')
    } finally {
      setIsSaving(false)
    }
  }

  const handleOpenCardEditor = card => {
    if (!canEdit || !card) return
    const addr = extractAddress(card)
    setEditingCard({
      ...card,
      phone: card.phone ? formatPhone(card.phone) : '',
      fax: card.fax ? formatPhone(card.fax) : '',
      address: {
        line1: addr.line1 || '',
        city: addr.city || '',
        state: addr.state || '',
        zip: addr.zip || '',
      },
      subContacts: (card.subContacts || []).map(sc => ({
        ...sc,
        phone: sc.phone ? formatPhone(sc.phone) : '',
      })),
    })
  }

  const handleSaveCardEdits = async e => {
    if (e) e.preventDefault()
    if (!editingCard || !caseModel || isSaving) return
    setIsSaving(true)
    try {
      const updatedCase = { ...caseModel }
      const cleanPhone = editingCard.phone ? formatPhone(editingCard.phone) : null
      const cleanFax = editingCard.fax ? formatPhone(editingCard.fax) : null
      const cleanEmail = (editingCard.email || '').trim() || null
      const cleanName = (editingCard.name || '').trim()
      const cleanAddress = buildCleanAddress(editingCard.address)
      const hasCleanAddress = Boolean(cleanAddress.line1 || cleanAddress.city || cleanAddress.state || cleanAddress.zip)
      const cleanedSubs = (editingCard.subContacts || [])
        .filter(sc => (sc.name && sc.name.trim()) || (sc.phone && sc.phone.trim()) || (sc.email && sc.email.trim()))
        .map(sc => ({
          ...sc,
          id: sc.id || `sub-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          name: (sc.name || '').trim() || 'Contact',
          role: (sc.role || '').trim() || 'Additional Contact',
          phone: sc.phone ? formatPhone(sc.phone) : null,
          extension: (sc.extension || '').trim() || null,
          email: (sc.email || '').trim() || null,
          directAddress: (sc.directAddress || '').trim() || null,
          notes: (sc.notes || '').trim() || null,
          isPrimary: Boolean(sc.isPrimary),
        }))

      if (editingCard.entityType === 'client') {
        const { firstName, lastName } = splitFullName(cleanName === 'Client / Claimant' ? '' : cleanName)
        const nextClient = {
          ...(updatedCase.client || {}),
          firstName: firstName || updatedCase.client?.firstName || '',
          lastName: lastName || updatedCase.client?.lastName || '',
          phone: cleanPhone,
          fax: cleanFax,
          email: cleanEmail,
          address: cleanAddress,
        }
        // First sub-contact maps to client.alternateContact; any further sub-contacts go to caseModel.keyContacts
        if (cleanedSubs.length > 0) {
          const altSub = cleanedSubs[0]
          const altParts = splitFullName(altSub.name)
          nextClient.hasAlternateContact = true
          nextClient.alternateContact = {
            ...(nextClient.alternateContact || {}),
            firstName: altParts.firstName,
            lastName: altParts.lastName,
            phone: altSub.phone,
            fax: altSub.fax || null,
            email: altSub.email,
          }
        } else {
          nextClient.hasAlternateContact = false
          nextClient.alternateContact = null
        }
        updatedCase.client = nextClient

        const extraClientSubs = cleanedSubs.slice(1)
        const otherKeyContacts = (updatedCase.keyContacts || []).filter(kc => kc.parentEntityKey !== 'entity:client')
        updatedCase.keyContacts = [
          ...otherKeyContacts,
          ...extraClientSubs.map(sc => ({
            ...sc,
            parentEntityKey: 'entity:client',
            organization: cleanName,
          })),
        ]
      } else if (editingCard.entityType === 'adjuster') {
        if (resolvedAdjuster && resolvedAdjuster.id) {
          const updatedAdj = {
            ...resolvedAdjuster,
            name: cleanName || resolvedAdjuster.name,
            phone: cleanPhone || '',
            fax: cleanFax || '',
            email: cleanEmail || '',
            addressLine1: cleanAddress.line1 || '',
            addressCity: cleanAddress.city || '',
            addressState: cleanAddress.state || '',
            addressZip: cleanAddress.zip || '',
          }
          await AdjustersService.update(updatedAdj)
          setResolvedAdjuster(updatedAdj)
        }
        if (editingCard.organization) {
          updatedCase.referral = {
            ...(updatedCase.referral || {}),
            insuranceCompany: editingCard.organization.trim(),
          }
        }
        const otherKeyContacts = (updatedCase.keyContacts || []).filter(kc => kc.parentEntityKey !== 'entity:adjuster')
        updatedCase.keyContacts = [
          ...otherKeyContacts,
          ...cleanedSubs.map(sc => ({
            ...sc,
            parentEntityKey: 'entity:adjuster',
            organization: editingCard.organization || cleanName,
          })),
        ]
      } else if (editingCard.entityType === 'provider' && editingCard.providerId) {
        const fullProv = await ProviderService.get(editingCard.providerId)
        const existingAddresses = Array.isArray(fullProv.addresses) ? [...fullProv.addresses] : []
        const primaryIdx = existingAddresses.findIndex(a => a && a.type === 'PrimaryPractice')
        const updatedAddressEntry = {
          ...(primaryIdx >= 0 ? existingAddresses[primaryIdx] : existingAddresses[0] || {}),
          type: primaryIdx >= 0 ? existingAddresses[primaryIdx].type : existingAddresses[0]?.type || 'PrimaryPractice',
          line1: cleanAddress.line1 || '',
          city: cleanAddress.city || '',
          state: cleanAddress.state || '',
          zip: cleanAddress.zip || '',
        }
        if (primaryIdx >= 0) {
          existingAddresses[primaryIdx] = updatedAddressEntry
        } else if (existingAddresses.length > 0) {
          existingAddresses[0] = updatedAddressEntry
        } else if (hasCleanAddress) {
          existingAddresses.push(updatedAddressEntry)
        }

        await ProviderService.update(editingCard.providerId, {
          ...fullProv,
          phone: cleanPhone,
          fax: cleanFax,
          email: cleanEmail,
          addresses: existingAddresses,
          notes: editingCard.notes || fullProv.notes || null,
          staffContacts: cleanedSubs.map(sc => ({
            id: sc.id,
            name: sc.name,
            role: sc.role,
            phone: sc.phone,
            extension: sc.extension,
            email: sc.email,
            directAddress: sc.directAddress,
            notes: sc.notes,
            isPrimary: sc.isPrimary,
          })),
        })

        // Also keep any mirrored case-level pharmacies / equipmentProviders / treatmentProviders in sync
        if (Array.isArray(updatedCase.pharmacies)) {
          updatedCase.pharmacies = updatedCase.pharmacies.map((p, idx) =>
            idx === editingCard.pharmacyIndex ||
            (p && String(p.id || p.providerId) === String(editingCard.providerId)) ||
            (p && normalizeStr(p.name) === normalizeStr(cleanName))
              ? {
                  ...p,
                  name: cleanName || p.name,
                  phone: cleanPhone,
                  fax: cleanFax,
                  email: cleanEmail,
                  address: cleanAddress,
                }
              : p,
          )
        }
        if (Array.isArray(updatedCase.equipmentProviders)) {
          updatedCase.equipmentProviders = updatedCase.equipmentProviders.map((eq, idx) =>
            idx === editingCard.equipmentIndex ||
            (eq && String(eq.id || eq.providerId) === String(editingCard.providerId)) ||
            (eq && normalizeStr(eq.name) === normalizeStr(cleanName))
              ? {
                  ...eq,
                  name: cleanName || eq.name,
                  phone: cleanPhone,
                  fax: cleanFax,
                  email: cleanEmail,
                  address: cleanAddress,
                }
              : eq,
          )
        }
        if (Array.isArray(updatedCase.treatmentProviders)) {
          updatedCase.treatmentProviders = updatedCase.treatmentProviders.map(tp =>
            tp &&
            (String(tp.id || tp.providerId) === String(editingCard.providerId) ||
              normalizeStr(tp.name) === normalizeStr(cleanName))
              ? {
                  ...tp,
                  name: cleanName || tp.name,
                  phone: cleanPhone,
                  fax: cleanFax,
                  email: cleanEmail,
                  address: cleanAddress,
                }
              : tp,
          )
        }

        // Remove any duplicate custom keyContacts that were merged into this provider's staffContacts
        if (Array.isArray(updatedCase.keyContacts)) {
          updatedCase.keyContacts = updatedCase.keyContacts.filter(
            kc =>
              kc.parentEntityKey !== editingCard.sourceKey &&
              String(kc.sourceEntityId || '') !== String(editingCard.providerId),
          )
        }
        await loadLinkedEntities()
      } else if (editingCard.entityType === 'pharmacy') {
        const existingPharms = Array.isArray(updatedCase.pharmacies) ? [...updatedCase.pharmacies] : []
        const targetIdx =
          typeof editingCard.pharmacyIndex === 'number'
            ? editingCard.pharmacyIndex
            : existingPharms.findIndex(p => p && normalizeStr(p.name) === normalizeStr(editingCard.name))
        const updatedPharm = {
          ...(targetIdx >= 0 ? existingPharms[targetIdx] : {}),
          name: cleanName || 'Pharmacy',
          phone: cleanPhone,
          fax: cleanFax,
          email: cleanEmail,
          address: cleanAddress,
        }
        if (targetIdx >= 0) {
          existingPharms[targetIdx] = updatedPharm
        } else {
          existingPharms.push(updatedPharm)
        }
        updatedCase.pharmacies = existingPharms
        const otherKeyContacts = (updatedCase.keyContacts || []).filter(
          kc => kc.parentEntityKey !== editingCard.sourceKey,
        )
        updatedCase.keyContacts = [
          ...otherKeyContacts,
          ...cleanedSubs.map(sc => ({
            ...sc,
            parentEntityKey: editingCard.sourceKey,
            organization: cleanName,
          })),
        ]
      } else if (editingCard.entityType === 'equipmentProvider') {
        const existingEq = Array.isArray(updatedCase.equipmentProviders) ? [...updatedCase.equipmentProviders] : []
        const targetIdx =
          typeof editingCard.equipmentIndex === 'number'
            ? editingCard.equipmentIndex
            : existingEq.findIndex(eq => eq && normalizeStr(eq.name) === normalizeStr(editingCard.name))
        const updatedEq = {
          ...(targetIdx >= 0 ? existingEq[targetIdx] : {}),
          name: cleanName || 'Equipment / DME Provider',
          phone: cleanPhone,
          fax: cleanFax,
          email: cleanEmail,
          address: cleanAddress,
        }
        if (targetIdx >= 0) {
          existingEq[targetIdx] = updatedEq
        } else {
          existingEq.push(updatedEq)
        }
        updatedCase.equipmentProviders = existingEq
        const otherKeyContacts = (updatedCase.keyContacts || []).filter(
          kc => kc.parentEntityKey !== editingCard.sourceKey,
        )
        updatedCase.keyContacts = [
          ...otherKeyContacts,
          ...cleanedSubs.map(sc => ({
            ...sc,
            parentEntityKey: editingCard.sourceKey,
            organization: cleanName,
          })),
        ]
      } else if (editingCard.entityType === 'caseProvider') {
        const existingTp = Array.isArray(updatedCase.treatmentProviders) ? [...updatedCase.treatmentProviders] : []
        const targetIdx =
          typeof editingCard.caseProviderIndex === 'number'
            ? editingCard.caseProviderIndex
            : existingTp.findIndex(tp => tp && normalizeStr(tp.name) === normalizeStr(editingCard.name))
        if (targetIdx >= 0 && existingTp[targetIdx]) {
          existingTp[targetIdx] = {
            ...existingTp[targetIdx],
            name: cleanName || existingTp[targetIdx].name,
            companyName: (editingCard.organization || '').trim() || existingTp[targetIdx].companyName || null,
            phone: cleanPhone,
            fax: cleanFax,
            email: cleanEmail,
            address: cleanAddress,
          }
          updatedCase.treatmentProviders = existingTp
        }
        const otherKeyContacts = (updatedCase.keyContacts || []).filter(
          kc => kc.parentEntityKey !== editingCard.sourceKey,
        )
        updatedCase.keyContacts = [
          ...otherKeyContacts,
          ...cleanedSubs.map(sc => ({
            ...sc,
            parentEntityKey: editingCard.sourceKey,
            organization: editingCard.organization || cleanName,
          })),
        ]
      } else if (editingCard.entityType === 'employer') {
        const { firstName, lastName } = splitFullName(cleanName)
        updatedCase.employer = {
          ...(updatedCase.employer || {}),
          name: (editingCard.organization || cleanName || '').trim() || null,
          address: cleanAddress,
          contact: {
            ...(updatedCase.employer?.contact || {}),
            firstName,
            lastName,
            title: editingCard.role || 'Employer Contact',
            phone: cleanPhone,
            fax: cleanFax,
            email: cleanEmail,
          },
        }
        const otherKeyContacts = (updatedCase.keyContacts || []).filter(kc => kc.parentEntityKey !== 'entity:employer')
        updatedCase.keyContacts = [
          ...otherKeyContacts,
          ...cleanedSubs.map(sc => ({
            ...sc,
            parentEntityKey: 'entity:employer',
            organization: editingCard.organization || cleanName,
          })),
        ]
      } else if (editingCard.entityType === 'plaintiff' || editingCard.entityType === 'defendant') {
        const targetField = editingCard.entityType
        const { firstName, lastName } = splitFullName(cleanName)
        updatedCase[targetField] = {
          ...(updatedCase[targetField] || {}),
          name: (editingCard.organization || cleanName || '').trim() || null,
          address: cleanAddress,
          contact: {
            ...(updatedCase[targetField]?.contact || {}),
            firstName,
            lastName,
            title: editingCard.role || 'Attorney',
            phone: cleanPhone,
            fax: cleanFax,
            email: cleanEmail,
          },
        }
        const otherKeyContacts = (updatedCase.keyContacts || []).filter(
          kc => kc.parentEntityKey !== editingCard.sourceKey,
        )
        updatedCase.keyContacts = [
          ...otherKeyContacts,
          ...cleanedSubs.map(sc => ({
            ...sc,
            parentEntityKey: editingCard.sourceKey,
            organization: editingCard.organization || cleanName,
          })),
        ]
      } else if (editingCard.entityType === 'physicianReviewer') {
        updatedCase.physicianReviewer = {
          ...(updatedCase.physicianReviewer || {}),
          name: cleanName || null,
          companyName: (editingCard.organization || '').trim() || null,
          contact: {
            ...(updatedCase.physicianReviewer?.contact || {}),
            phone: cleanPhone,
            fax: cleanFax,
            email: cleanEmail,
          },
        }
      } else {
        // Custom or ancillary card
        const existingList = Array.isArray(updatedCase.keyContacts) ? [...updatedCase.keyContacts] : []
        const updatedItem = {
          id: editingCard.id || `kc-${Date.now()}`,
          name: cleanName,
          role: editingCard.role || 'Key Contact',
          category: editingCard.category || 'ANCILLARY',
          organization: (editingCard.organization || '').trim() || null,
          phone: cleanPhone,
          extension: (editingCard.extension || '').trim() || null,
          fax: cleanFax,
          email: cleanEmail,
          directAddress: (editingCard.directAddress || '').trim() || null,
          address: hasCleanAddress ? cleanAddress : null,
          notes: (editingCard.notes || '').trim() || null,
          isPrimary: Boolean(editingCard.isPrimary),
          subContacts: cleanedSubs,
        }
        if (typeof editingCard.customIndex === 'number' && existingList[editingCard.customIndex]) {
          existingList[editingCard.customIndex] = updatedItem
        } else {
          existingList.push(updatedItem)
        }
        updatedCase.keyContacts = existingList
      }

      await CasesService.update(updatedCase)
      if (setCaseModel) setCaseModel(updatedCase)
      setEditingCard(null)
      cogoToast.success(`Updated "${cleanName || 'Contact'}"`)
    } catch (err) {
      cogoToast.error(err.message || 'Failed to save contact changes')
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddContactSubmit = async e => {
    if (e) e.preventDefault()
    if (!newContact.name.trim()) {
      cogoToast.warn('Please enter a contact name')
      return
    }
    setIsSaving(true)
    try {
      const parentCard = visibleCards.find(c => c.sourceKey === newContact.parentCardKey)
      const orgLabel = newContact.organization.trim() || (parentCard ? parentCard.organization || parentCard.name : '')
      const cleanNewAddress = buildCleanAddress(newContact.address)
      const hasNewAddress = Boolean(
        cleanNewAddress.line1 || cleanNewAddress.city || cleanNewAddress.state || cleanNewAddress.zip,
      )

      const formattedEntry = {
        id: `kc-${Date.now()}`,
        name: newContact.name.trim(),
        role: newContact.role || 'Other',
        category: parentCard ? parentCard.category : newContact.category || 'MEDICAL',
        organization: orgLabel || null,
        parentEntityKey: parentCard ? parentCard.sourceKey : null,
        sourceEntityId: parentCard?.providerId ? String(parentCard.providerId) : null,
        phone: newContact.phone ? formatPhone(newContact.phone) : null,
        extension: newContact.extension.trim() || null,
        fax: newContact.fax ? formatPhone(newContact.fax) : null,
        email: newContact.email.trim() || null,
        directAddress: newContact.directAddress.trim() || null,
        address: !parentCard && hasNewAddress ? cleanNewAddress : null,
        notes: newContact.notes.trim() || null,
        isPrimary: Boolean(newContact.isPrimary),
      }

      // If grouped under a Treating Provider card and saveToProviderDirectory is checked, save directly to Provider.staffContacts
      if (parentCard?.entityType === 'provider' && parentCard.providerId && newContact.saveToProviderDirectory) {
        try {
          const fullProv = await ProviderService.get(parentCard.providerId)
          const existingStaff = Array.isArray(fullProv.staffContacts) ? fullProv.staffContacts : []
          const updatedStaff = [
            ...existingStaff,
            {
              id: formattedEntry.id,
              name: formattedEntry.name,
              role: formattedEntry.role,
              phone: formattedEntry.phone,
              extension: formattedEntry.extension,
              email: formattedEntry.email,
              directAddress: formattedEntry.directAddress,
              notes: formattedEntry.notes,
              isPrimary: formattedEntry.isPrimary,
            },
          ]
          await ProviderService.update(parentCard.providerId, {
            ...fullProv,
            staffContacts: updatedStaff,
          })
          await loadLinkedEntities()
        } catch {
          // Fallback to caseModel.keyContacts below
        }
      } else {
        const existingKeyContacts = Array.isArray(caseModel?.keyContacts) ? [...caseModel.keyContacts] : []
        const updatedCase = {
          ...caseModel,
          keyContacts: [...existingKeyContacts, formattedEntry],
        }
        await CasesService.update(updatedCase)
        if (setCaseModel) setCaseModel(updatedCase)
      }

      setShowAddModal(false)
      setNewContact({
        name: '',
        role: 'Medical Assistant',
        category: 'MEDICAL',
        organization: '',
        parentCardKey: '',
        saveToProviderDirectory: true,
        phone: '',
        extension: '',
        fax: '',
        email: '',
        directAddress: '',
        address: { line1: '', city: '', state: '', zip: '' },
        notes: '',
        isPrimary: false,
      })
      cogoToast.success(
        parentCard
          ? `Added "${formattedEntry.name}" under ${parentCard.name}`
          : `Added "${formattedEntry.name}" to Key Contacts`,
      )
    } catch (err) {
      cogoToast.error(err.message || 'Failed to add contact')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="container-fluid mb-4" style={{ overflowAnchor: 'none' }} data-testid="key-contacts-card">
      <Card className="shadow-sm border-0">
        <Card.Header
          className="bg-white d-flex justify-content-between align-items-center py-2 px-3 border-bottom flex-wrap"
          data-testid="key-contacts-header"
        >
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none text-dark d-flex align-items-center my-1"
            onClick={toggleSectionExpanded}
          >
            <FAIcon name={isSectionExpanded ? 'chevron-down' : 'chevron-right'} className="text-muted mr-2" />
            <FAIcon name="address-book" className="text-primary mr-2" />
            <h6 className="mb-0 font-weight-bold">
              Key Contacts at a Glance{' '}
              <Badge variant="light" className="ml-1 border">
                {visibleCards.length}
              </Badge>
            </h6>
            {!isSectionExpanded && (
              <div className="d-none d-md-flex align-items-center ml-3 text-muted small">
                {Object.entries(availableCategories).map(([catKey, count]) => {
                  const catMeta = CONTACT_CATEGORIES[catKey] || CONTACT_CATEGORIES.ANCILLARY
                  return (
                    <Badge key={catKey} variant="light" className="mr-1 border font-weight-normal">
                      <FAIcon name={catMeta.icon} className="mr-1" />
                      {catMeta.label}: {count}
                    </Badge>
                  )
                })}
              </div>
            )}
          </button>

          <div className="d-flex align-items-center my-1 flex-wrap">
            {isSectionExpanded && Object.keys(availableCategories).length > 1 && (
              <div className="btn-group btn-group-sm mr-2 d-none d-lg-inline-flex">
                <Button
                  variant={categoryFilter === 'ALL' ? 'secondary' : 'outline-secondary'}
                  size="sm"
                  className="py-0 px-2"
                  onClick={() => setCategoryFilter('ALL')}
                >
                  All
                </Button>
                {Object.keys(CONTACT_CATEGORIES).map(catKey => {
                  if (!availableCategories[catKey]) return null
                  const cat = CONTACT_CATEGORIES[catKey]
                  return (
                    <Button
                      key={catKey}
                      variant={categoryFilter === catKey ? 'secondary' : 'outline-secondary'}
                      size="sm"
                      className="py-0 px-2"
                      onClick={() => setCategoryFilter(catKey)}
                    >
                      <FAIcon name={cat.icon} className="mr-1" />
                      {cat.label}
                    </Button>
                  )
                })}
              </div>
            )}
            {hiddenCards.length > 0 && canEdit && (
              <Button
                variant="link"
                size="sm"
                className="text-muted p-0 mr-3"
                onClick={handleRestoreHiddenContacts}
                disabled={isSaving}
                title="Restore contacts previously hidden on this case"
              >
                Restore Hidden ({hiddenCards.length})
              </Button>
            )}
            {canEdit && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowAddModal(true)}
                data-testid="add-key-contact-btn"
              >
                <FAIcon name="plus" className="mr-1" /> Add Contact
              </Button>
            )}
          </div>
        </Card.Header>

        {isSectionExpanded && (
          <Card.Body className="py-2 px-3">
            {filteredCards.length === 0 ? (
              <div className="text-muted small py-2">No contacts match the selected filter.</div>
            ) : (
              <Row className="mx-n1">
                {filteredCards.map(card => {
                  const catMeta = CONTACT_CATEGORIES[card.category] || CONTACT_CATEGORIES.ANCILLARY
                  const cardIcon = card.iconOverride || catMeta.icon
                  const telHref = card.phone
                    ? `tel:${String(card.phone).replace(/[^\d+]/g, '')}${card.extension ? `,${card.extension}` : ''}`
                    : null
                  const subContacts = Array.isArray(card.subContacts) ? card.subContacts : []
                  const formattedAddr = formatAddress(card.address)
                  const hasContactDetails = Boolean(
                    card.phone ||
                      card.email ||
                      formattedAddr ||
                      card.fax ||
                      card.directAddress ||
                      subContacts.length > 0,
                  )

                  return (
                    <Col key={card.key} sm={12} md={6} lg={4} xl={3} className="px-1 my-1">
                      <div
                        role="button"
                        tabIndex={0}
                        className="border rounded p-2 h-100 bg-white shadow-sm d-flex flex-column justify-content-between"
                        style={{
                          borderTop: `3px solid ${catMeta.accentColor}`,
                          cursor: 'pointer',
                          transition: 'box-shadow 0.15s ease-in-out',
                        }}
                        onClick={e => handleOpenDetailModal(card, e)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleOpenDetailModal(card, e)
                          }
                        }}
                        title="Click to open full contact card & staff details"
                        data-testid={`contact-card-${card.sourceKey}`}
                      >
                        <div>
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex align-items-start pr-1" style={{ minWidth: 0 }}>
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center mr-2 mt-1 flex-shrink-0"
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  backgroundColor: `${catMeta.accentColor}18`,
                                  color: catMeta.accentColor,
                                }}
                              >
                                <FAIcon name={cardIcon} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <span
                                  className="font-weight-bold text-dark d-block text-truncate"
                                  style={{ fontSize: '0.92rem', lineHeight: '1.2' }}
                                  title={card.name}
                                >
                                  {card.name}
                                </span>
                                <div className="mt-1">
                                  <Badge variant={catMeta.badgeVariant} className="mr-1">
                                    {card.role}
                                  </Badge>
                                  {card.isPrimary && (
                                    <Badge variant="light" className="border text-secondary">
                                      Primary
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="d-flex align-items-center flex-shrink-0 ml-1">
                              {canEdit && (
                                <>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-muted mr-2"
                                    title="Edit contact & sub-contacts"
                                    onClick={e => {
                                      e.stopPropagation()
                                      handleOpenCardEditor(card)
                                    }}
                                    data-testid={`edit-contact-btn-${card.sourceKey}`}
                                  >
                                    <FAIcon name="pencil-alt" />
                                  </Button>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-muted"
                                    title="Hide from this case's Key Contacts (does not delete underlying record)"
                                    onClick={e => handleHideOrRemoveCard(card, e)}
                                    disabled={isSaving}
                                  >
                                    <FAIcon name="times" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>

                          {card.organization && (
                            <div className="text-muted small mt-1 text-truncate" title={card.organization}>
                              <FAIcon name="building" className="mr-1 text-secondary" />
                              {card.organization}
                            </div>
                          )}
                        </div>

                        <div className="mt-2 pt-1 border-top small">
                          {!hasContactDetails && (
                            <div className="d-flex justify-content-between align-items-center py-1">
                              <span className="text-muted font-italic">No contact info added yet</span>
                              {canEdit && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="py-0 px-2"
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleOpenCardEditor(card)
                                  }}
                                >
                                  <FAIcon name="pencil-alt" className="mr-1" /> Add Info
                                </Button>
                              )}
                            </div>
                          )}

                          {card.phone && (
                            <div className="d-flex align-items-center justify-content-between text-truncate">
                              <div className="text-truncate">
                                <FAIcon name="phone" className="mr-1 text-success" />
                                <a
                                  href={telHref}
                                  className="text-dark font-weight-bold"
                                  onClick={e => e.stopPropagation()}
                                >
                                  {formatPhone(card.phone)}
                                </a>
                                {card.extension && <span className="text-muted ml-1">ext. {card.extension}</span>}
                              </div>
                              {canEdit && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  className="py-0 px-1 ml-1 flex-shrink-0"
                                  style={{ fontSize: '0.7rem', lineHeight: 1.3 }}
                                  title={`Log 0.2h T-Con activity with ${card.name}`}
                                  data-testid={`log-call-btn-${card.sourceKey}`}
                                  disabled={loggingCallKey === card.sourceKey}
                                  onClick={e => handleLogCallActivity(card, e)}
                                >
                                  <FAIcon name="stopwatch" className="mr-1" />
                                  {loggingCallKey === card.sourceKey ? 'Logging...' : '+0.2h Call'}
                                </Button>
                              )}
                            </div>
                          )}

                          {card.email && (
                            <div className="text-truncate mt-1">
                              <FAIcon name="envelope" className="mr-1 text-primary" />
                              <a href={`mailto:${card.email}`} title={card.email} onClick={e => e.stopPropagation()}>
                                {card.email}
                              </a>
                            </div>
                          )}

                          {formattedAddr && (
                            <div className="text-muted text-truncate mt-1" title={formattedAddr}>
                              <FAIcon name="map-marker-alt" className="mr-1 text-danger" />
                              {formattedAddr}
                            </div>
                          )}

                          {subContacts.length > 0 && (
                            <div className="mt-1 pt-1 border-top d-flex justify-content-between align-items-center text-primary">
                              <span className="font-weight-bold text-truncate" style={{ fontSize: '0.76rem' }}>
                                <FAIcon name="users" className="mr-1" />+{subContacts.length} Additional{' '}
                                {subContacts.length === 1 ? 'Contact' : 'Contacts'} ({subContacts[0].name}
                                {subContacts.length > 1 ? ', ...' : ''})
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  )
                })}
              </Row>
            )}
          </Card.Body>
        )}
      </Card>

      {/* POP-UP DETAIL CARD MODAL (VIEW & ACT ON CONTACTS) */}
      <Modal
        show={Boolean(viewingCard)}
        onHide={() => setViewingCardKey(null)}
        centered
        data-testid="contact-detail-modal"
      >
        {viewingCard &&
          (() => {
            const detailCat = CONTACT_CATEGORIES[viewingCard.category] || CONTACT_CATEGORIES.ANCILLARY
            const detailIcon = viewingCard.iconOverride || detailCat.icon
            const detailTelHref = viewingCard.phone
              ? `tel:${String(viewingCard.phone).replace(/[^\d+]/g, '')}${
                  viewingCard.extension ? `,${viewingCard.extension}` : ''
                }`
              : null
            const detailAddr = formatAddress(viewingCard.address)
            const detailSubs = Array.isArray(viewingCard.subContacts) ? viewingCard.subContacts : []

            return (
              <>
                <Modal.Header
                  closeButton
                  style={{ borderTop: `4px solid ${detailCat.accentColor}` }}
                  className="align-items-start"
                >
                  <Modal.Title className="d-flex align-items-center">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mr-2 flex-shrink-0"
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: `${detailCat.accentColor}18`,
                        color: detailCat.accentColor,
                      }}
                    >
                      <FAIcon name={detailIcon} />
                    </div>
                    <div>
                      <div className="font-weight-bold text-dark" style={{ fontSize: '1.08rem', lineHeight: '1.2' }}>
                        {viewingCard.name}
                      </div>
                      <div className="mt-1">
                        <Badge variant={detailCat.badgeVariant} className="mr-1">
                          {viewingCard.role}
                        </Badge>
                        {viewingCard.isPrimary && (
                          <Badge variant="light" className="border text-secondary">
                            Primary
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  {viewingCard.organization && (
                    <div className="mb-3 text-muted">
                      <FAIcon name="building" className="mr-2 text-secondary" />
                      <strong>{viewingCard.organization}</strong>
                    </div>
                  )}

                  <div className="border rounded p-3 bg-light mb-3">
                    <h6 className="text-uppercase text-muted small font-weight-bold mb-2">Contact Details</h6>

                    {!viewingCard.phone &&
                      !viewingCard.fax &&
                      !viewingCard.email &&
                      !viewingCard.directAddress &&
                      !detailAddr &&
                      !viewingCard.notes && (
                        <div className="text-muted font-italic small">No primary contact details entered yet.</div>
                      )}

                    {viewingCard.phone && (
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <div>
                          <FAIcon name="phone" className="mr-2 text-success" />
                          <a href={detailTelHref} className="text-dark font-weight-bold">
                            {formatPhone(viewingCard.phone)}
                          </a>
                          {viewingCard.extension && (
                            <span className="text-muted ml-1">ext. {viewingCard.extension}</span>
                          )}
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          title="Copy phone number"
                          onClick={e => handleCopyValue(formatPhone(viewingCard.phone), 'phone number', e)}
                        >
                          <FAIcon name="copy" />
                        </Button>
                      </div>
                    )}

                    {viewingCard.fax && (
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <div>
                          <FAIcon name="fax" className="mr-2 text-secondary" />
                          <span>Fax: {formatPhone(viewingCard.fax)}</span>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          title="Copy fax number"
                          onClick={e => handleCopyValue(formatPhone(viewingCard.fax), 'fax number', e)}
                        >
                          <FAIcon name="copy" />
                        </Button>
                      </div>
                    )}

                    {viewingCard.email && (
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <div className="text-truncate pr-2">
                          <FAIcon name="envelope" className="mr-2 text-primary" />
                          <a href={`mailto:${viewingCard.email}`}>{viewingCard.email}</a>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          title="Copy email address"
                          onClick={e => handleCopyValue(viewingCard.email, 'email address', e)}
                        >
                          <FAIcon name="copy" />
                        </Button>
                      </div>
                    )}

                    {viewingCard.directAddress && (
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <div className="text-info text-truncate pr-2">
                          <FAIcon name="shield-alt" className="mr-2" />
                          <span>Direct: {viewingCard.directAddress}</span>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          title="Copy Direct Address"
                          onClick={e => handleCopyValue(viewingCard.directAddress, 'Direct Address', e)}
                        >
                          <FAIcon name="copy" />
                        </Button>
                      </div>
                    )}

                    {detailAddr && (
                      <div className="d-flex justify-content-between align-items-start py-1">
                        <div className="pr-2">
                          <FAIcon name="map-marker-alt" className="mr-2 text-danger" />
                          <span>{detailAddr}</span>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailAddr)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 small"
                          >
                            Map <FAIcon name="external-link-alt" />
                          </a>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          title="Copy address"
                          onClick={e => handleCopyValue(detailAddr, 'address', e)}
                        >
                          <FAIcon name="copy" />
                        </Button>
                      </div>
                    )}

                    {viewingCard.notes && (
                      <div className="mt-2 pt-2 border-top text-muted small" style={{ whiteSpace: 'pre-wrap' }}>
                        <FAIcon name="sticky-note" className="mr-1 text-secondary" />
                        <em>{viewingCard.notes}</em>
                      </div>
                    )}
                  </div>

                  {detailSubs.length > 0 && (
                    <div>
                      <h6 className="text-uppercase text-muted small font-weight-bold mb-2">
                        <FAIcon name="users" className="mr-1" />
                        Additional Contacts ({detailSubs.length})
                      </h6>
                      <div className="border rounded bg-white">
                        {detailSubs.map((sc, sIdx) => {
                          const scTelHref = sc.phone
                            ? `tel:${String(sc.phone).replace(/[^\d+]/g, '')}${sc.extension ? `,${sc.extension}` : ''}`
                            : null
                          return (
                            <div key={sc.id || sIdx} className={`p-2 ${sIdx > 0 ? 'border-top' : ''}`}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="font-weight-bold text-dark">{sc.name}</span>
                                <Badge variant="secondary" className="font-weight-normal">
                                  {sc.role}
                                </Badge>
                              </div>
                              <div className="d-flex flex-wrap align-items-center small mt-1">
                                {sc.phone && (
                                  <span className="mr-3">
                                    <FAIcon name="phone" className="mr-1 text-success" />
                                    <a href={scTelHref} className="text-dark font-weight-bold">
                                      {formatPhone(sc.phone)}
                                      {sc.extension ? ` ext. ${sc.extension}` : ''}
                                    </a>
                                  </span>
                                )}
                                {sc.email && (
                                  <span className="mr-3">
                                    <FAIcon name="envelope" className="mr-1 text-primary" />
                                    <a href={`mailto:${sc.email}`}>{sc.email}</a>
                                  </span>
                                )}
                                {sc.directAddress && (
                                  <span className="text-info">
                                    <FAIcon name="shield-alt" className="mr-1" />
                                    {sc.directAddress}
                                  </span>
                                )}
                              </div>
                              {sc.notes && (
                                <div className="text-muted small mt-1">
                                  <em>{sc.notes}</em>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between">
                  <Button variant="secondary" size="sm" onClick={() => setViewingCardKey(null)}>
                    Close
                  </Button>
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    {canEdit && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        data-testid="detail-modal-log-call-btn"
                        disabled={loggingCallKey === viewingCard.sourceKey}
                        onClick={e => handleLogCallActivity(viewingCard, e)}
                      >
                        <FAIcon name="phone" className="mr-1" />
                        {loggingCallKey === viewingCard.sourceKey ? 'Logging...' : 'Log Call (0.2h)'}
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="primary"
                        size="sm"
                        data-testid="detail-modal-edit-btn"
                        onClick={() => {
                          const target = viewingCard
                          setViewingCardKey(null)
                          handleOpenCardEditor(target)
                        }}
                      >
                        <FAIcon name="pencil-alt" className="mr-1" /> Edit Contact &amp; Staff
                      </Button>
                    )}
                  </div>
                </Modal.Footer>
              </>
            )
          })()}
      </Modal>

      {/* EDIT CONTACT & SUB-CONTACTS MODAL */}
      <Modal
        show={Boolean(editingCard)}
        onHide={() => setEditingCard(null)}
        onEntered={() => editNameInputRef.current?.focus()}
        size="lg"
        centered
      >
        {editingCard && (
          <Form onSubmit={handleSaveCardEdits}>
            <Modal.Header closeButton>
              <Modal.Title className="d-flex align-items-center">
                <FAIcon
                  name={(CONTACT_CATEGORIES[editingCard.category] || CONTACT_CATEGORIES.ANCILLARY).icon}
                  className="text-primary mr-2"
                />
                Edit Contact — {editingCard.name}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <h6 className="text-uppercase text-muted small font-weight-bold mb-2">Primary Contact Details</h6>
              <Row>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Name *</Form.Label>
                    <Form.Control
                      ref={editNameInputRef}
                      size="sm"
                      value={editingCard.name || ''}
                      onChange={e => setEditingCard({ ...editingCard, name: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Role / Title</Form.Label>
                    <Form.Control
                      size="sm"
                      value={editingCard.role || ''}
                      onChange={e => setEditingCard({ ...editingCard, role: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Organization / Clinic</Form.Label>
                    <Form.Control
                      size="sm"
                      value={editingCard.organization || ''}
                      onChange={e => setEditingCard({ ...editingCard, organization: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Phone</Form.Label>
                    <InputMask
                      mask="(999) 999-9999"
                      className="form-control form-control-sm"
                      type="tel"
                      value={editingCard.phone || ''}
                      onChange={e => setEditingCard({ ...editingCard, phone: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Ext.</Form.Label>
                    <Form.Control
                      size="sm"
                      value={editingCard.extension || ''}
                      onChange={e => setEditingCard({ ...editingCard, extension: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Fax</Form.Label>
                    <InputMask
                      mask="(999) 999-9999"
                      className="form-control form-control-sm"
                      type="tel"
                      value={editingCard.fax || ''}
                      onChange={e => setEditingCard({ ...editingCard, fax: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Email</Form.Label>
                    <Form.Control
                      size="sm"
                      type="email"
                      value={editingCard.email || ''}
                      onChange={e => setEditingCard({ ...editingCard, email: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Street Address</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="123 Main St, Suite 100"
                      value={editingCard.address?.line1 || ''}
                      onChange={e =>
                        setEditingCard({
                          ...editingCard,
                          address: { ...(editingCard.address || {}), line1: e.target.value },
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small mb-1">City</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="City"
                      value={editingCard.address?.city || ''}
                      onChange={e =>
                        setEditingCard({
                          ...editingCard,
                          address: { ...(editingCard.address || {}), city: e.target.value },
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small mb-1">State</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="ST"
                      value={editingCard.address?.state || ''}
                      onChange={e =>
                        setEditingCard({
                          ...editingCard,
                          address: { ...(editingCard.address || {}), state: e.target.value },
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Zip</Form.Label>
                    <Form.Control
                      size="sm"
                      placeholder="Zip"
                      value={editingCard.address?.zip || ''}
                      onChange={e =>
                        setEditingCard({
                          ...editingCard,
                          address: { ...(editingCard.address || {}), zip: e.target.value },
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Notes</Form.Label>
                    <Form.Control
                      size="sm"
                      value={editingCard.notes || ''}
                      onChange={e => setEditingCard({ ...editingCard, notes: e.target.value })}
                      placeholder="Claim #, best hours, direct fax instructions, etc."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-3" />
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h6 className="text-uppercase text-muted small font-weight-bold mb-0">
                    Additional Contacts ({(editingCard.subContacts || []).length})
                  </h6>
                  <small className="text-muted">
                    {editingCard.entityType === 'provider'
                      ? 'Medical assistants, referral coordinators, schedulers, records clerks, and billing staff for this provider'
                      : editingCard.entityType === 'client'
                      ? 'Alternate / family / emergency contacts for the claimant'
                      : 'Additional staff, assistants, or department contacts grouped on this card'}
                  </small>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() =>
                    setEditingCard({
                      ...editingCard,
                      subContacts: [
                        ...(editingCard.subContacts || []),
                        {
                          id: `sub-${Date.now()}`,
                          name: '',
                          role:
                            editingCard.entityType === 'provider'
                              ? 'Medical Assistant'
                              : editingCard.entityType === 'client'
                              ? 'Alternate / Emergency Contact'
                              : editingCard.entityType === 'plaintiff' || editingCard.entityType === 'defendant'
                              ? 'Paralegal'
                              : 'Assistant',
                          phone: '',
                          extension: '',
                          email: '',
                          directAddress: '',
                          notes: '',
                        },
                      ],
                    })
                  }
                >
                  <FAIcon name="plus" className="mr-1" /> Add Sub-Contact
                </Button>
              </div>

              {(editingCard.subContacts || []).map((sc, sIdx) => (
                <div key={sc.id || sIdx} className="border rounded p-2 mb-2 bg-light">
                  <Row className="align-items-end">
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label className="small mb-0">Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={sc.name || ''}
                          placeholder="e.g. Sarah Jenkins"
                          onChange={e => {
                            const next = [...editingCard.subContacts]
                            next[sIdx] = { ...next[sIdx], name: e.target.value }
                            setEditingCard({ ...editingCard, subContacts: next })
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-1">
                        <Form.Label className="small mb-0">Role</Form.Label>
                        <Form.Control
                          size="sm"
                          value={sc.role || ''}
                          placeholder="e.g. Medical Assistant"
                          onChange={e => {
                            const next = [...editingCard.subContacts]
                            next[sIdx] = { ...next[sIdx], role: e.target.value }
                            setEditingCard({ ...editingCard, subContacts: next })
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-1">
                        <Form.Label className="small mb-0">Phone</Form.Label>
                        <InputMask
                          mask="(999) 999-9999"
                          className="form-control form-control-sm"
                          type="tel"
                          value={sc.phone || ''}
                          onChange={e => {
                            const next = [...editingCard.subContacts]
                            next[sIdx] = { ...next[sIdx], phone: e.target.value }
                            setEditingCard({ ...editingCard, subContacts: next })
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-1">
                        <Form.Label className="small mb-0">Ext.</Form.Label>
                        <Form.Control
                          size="sm"
                          value={sc.extension || ''}
                          onChange={e => {
                            const next = [...editingCard.subContacts]
                            next[sIdx] = { ...next[sIdx], extension: e.target.value }
                            setEditingCard({ ...editingCard, subContacts: next })
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="align-items-end mt-1">
                    <Col md={5}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small mb-0">Email</Form.Label>
                        <Form.Control
                          size="sm"
                          type="email"
                          value={sc.email || ''}
                          onChange={e => {
                            const next = [...editingCard.subContacts]
                            next[sIdx] = { ...next[sIdx], email: e.target.value }
                            setEditingCard({ ...editingCard, subContacts: next })
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small mb-0">Notes / Direct Address</Form.Label>
                        <Form.Control
                          size="sm"
                          value={sc.notes || sc.directAddress || ''}
                          onChange={e => {
                            const next = [...editingCard.subContacts]
                            next[sIdx] = { ...next[sIdx], notes: e.target.value }
                            setEditingCard({ ...editingCard, subContacts: next })
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} className="text-right">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          const next = editingCard.subContacts.filter((_, i) => i !== sIdx)
                          setEditingCard({ ...editingCard, subContacts: next })
                        }}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setEditingCard(null)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* ADD NEW CONTACT OR SUB-CONTACT MODAL */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onEntered={() => nameInputRef.current?.focus()}
        centered
      >
        <Form onSubmit={handleAddContactSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add Key Contact</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Group Under Existing Card (Optional)</Form.Label>
              <Form.Control
                as="select"
                value={newContact.parentCardKey}
                onChange={e =>
                  setNewContact({
                    ...newContact,
                    parentCardKey: e.target.value,
                  })
                }
              >
                <option value="">-- Create as a standalone contact card --</option>
                {visibleCards.map(c => (
                  <option key={c.sourceKey} value={c.sourceKey}>
                    {c.name} ({c.role})
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                Select an existing Provider, Adjuster, Client, or Attorney card to group this person inside that card.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Contact Name *</Form.Label>
                  <Form.Control
                    ref={nameInputRef}
                    value={newContact.name}
                    onChange={e => setNewContact({ ...newContact, name: e.target.value })}
                    placeholder="e.g. Sarah Jenkins"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    as="select"
                    value={newContact.role}
                    onChange={e => setNewContact({ ...newContact, role: e.target.value })}
                  >
                    <option value="Medical Assistant">Medical Assistant (MA)</option>
                    <option value="Referral Coordinator">Referral / Auth Coordinator</option>
                    <option value="Scheduler">Scheduler</option>
                    <option value="Records Clerk">Medical Records</option>
                    <option value="Office Manager">Office Manager</option>
                    <option value="Billing">Billing / Workers Comp</option>
                    <option value="Case Coordinator">Nurse / Case Coordinator</option>
                    <option value="Claims Adjuster">Claims Adjuster</option>
                    <option value="Nurse Reviewer">Carrier Nurse Reviewer / UR</option>
                    <option value="Employer Contact">Employer / HR Contact</option>
                    <option value="Claimant Attorney">Claimant / Plaintiff Attorney</option>
                    <option value="Defense Attorney">Defense Attorney</option>
                    <option value="Paralegal">Paralegal / Legal Assistant</option>
                    <option value="Pharmacy">Pharmacy</option>
                    <option value="DME / Equipment">DME / Equipment Coordinator</option>
                    <option value="Interpreter">Interpreter / Translation</option>
                    <option value="Medical Transport">Medical Transportation</option>
                    <option value="Alternate / Emergency Contact">Family / Emergency Contact</option>
                    <option value="Other">Other</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            {newContact.parentCardKey.startsWith('provider:') && (
              <Form.Check
                type="checkbox"
                id="save-to-provider-directory"
                className="mb-3 small"
                label="Also save to this Provider's Staff & Office Contacts directory for future cases"
                checked={Boolean(newContact.saveToProviderDirectory)}
                onChange={e =>
                  setNewContact({
                    ...newContact,
                    saveToProviderDirectory: e.target.checked,
                  })
                }
              />
            )}

            {!newContact.parentCardKey && (
              <Form.Group>
                <Form.Label>Organization / Clinic / Firm</Form.Label>
                <Form.Control
                  value={newContact.organization}
                  onChange={e => setNewContact({ ...newContact, organization: e.target.value })}
                  placeholder="e.g. Summit Orthopedics"
                />
              </Form.Group>
            )}

            <Row>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Direct Phone</Form.Label>
                  <InputMask
                    mask="(999) 999-9999"
                    className="form-control"
                    type="tel"
                    value={newContact.phone}
                    onChange={e => setNewContact({ ...newContact, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Extension</Form.Label>
                  <Form.Control
                    value={newContact.extension}
                    onChange={e => setNewContact({ ...newContact, extension: e.target.value })}
                    placeholder="e.g. 204"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={newContact.email}
                    onChange={e => setNewContact({ ...newContact, email: e.target.value })}
                    placeholder="contact@clinic.org"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Direct Address (EHR)</Form.Label>
                  <Form.Control
                    value={newContact.directAddress}
                    onChange={e => setNewContact({ ...newContact, directAddress: e.target.value })}
                    placeholder="staff@direct.clinic.org"
                  />
                </Form.Group>
              </Col>
            </Row>

            {!newContact.parentCardKey && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Street Address</Form.Label>
                  <Form.Control
                    size="sm"
                    value={newContact.address?.line1 || ''}
                    onChange={e =>
                      setNewContact({
                        ...newContact,
                        address: { ...(newContact.address || {}), line1: e.target.value },
                      })
                    }
                    placeholder="123 Main St"
                  />
                </Form.Group>
                <Row>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label className="small mb-1">City</Form.Label>
                      <Form.Control
                        size="sm"
                        value={newContact.address?.city || ''}
                        onChange={e =>
                          setNewContact({
                            ...newContact,
                            address: { ...(newContact.address || {}), city: e.target.value },
                          })
                        }
                        placeholder="City"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small mb-1">State</Form.Label>
                      <Form.Control
                        size="sm"
                        value={newContact.address?.state || ''}
                        onChange={e =>
                          setNewContact({
                            ...newContact,
                            address: { ...(newContact.address || {}), state: e.target.value },
                          })
                        }
                        placeholder="ST"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small mb-1">Zip</Form.Label>
                      <Form.Control
                        size="sm"
                        value={newContact.address?.zip || ''}
                        onChange={e =>
                          setNewContact({
                            ...newContact,
                            address: { ...(newContact.address || {}), zip: e.target.value },
                          })
                        }
                        placeholder="Zip"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                value={newContact.notes}
                onChange={e => setNewContact({ ...newContact, notes: e.target.value })}
                placeholder="Best hours, direct fax line, etc."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Contact'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default KeyContactsCard
